<template>
    <button
        class="vf-dropdown-button vf-no-outline"
        type="button"
        :class="classes"
        :disabled="disabled"
        @mouseup="handleRelease"
        @mouseout="handleRelease"
        @keydown.space.enter="handleKeydown"
        @mousedown.prevent="handleClick"
        @click="$emit('click', $event)"
    >
        <!-- @slot WARNING: Only use this slot if high degrees of customization are required.
        Be sure to bind any needed scope to the content of the slot-->
        <slot
            name="icon"
            v-bind="iconProps"
            :is-active="isActive"
        >
            <component
                v-if="icon"
                :is="icon"
                v-bind="iconProps"
            />
        </slot>
        <span class="vf-dropdown-button__text">
            <slot />
        </span>
    </button>
</template>

<script>
import { PALETTES } from 'design-assets/constants/palettes.js';
import { SIZES } from 'design-assets/constants/sizes.js';

// Num ms to hold the active state after release
const TRANSITION_EXIT_WAIT = 180;

const VALID_PALETTES = [ PALETTES.ACCENT ];

const VALID_SIZES = [ SIZES.SMALL, SIZES.MEDIUM ];

export default {
    props: {
        /**
         * Simplest way to inject an icon. Will ensure consistent sizing and attach any necessary classes
         * to the icon. (RECOMMENDED)
         */
        icon: Object,
        disabled: Boolean,
        palette: {
            type: String,
            default: PALETTES.ACCENT,
            validator(palette) {
                return VALID_PALETTES.includes(palette);
            },
        },
        /**
         * Aligns the icon to the right of the button
         */
        alignIconRight: Boolean,
        /**
         * If "true" the button will show "hover" styles. Otherwise it will show styles for the current state of
         * hover, focus, or active. Note that focus overrides this.
         */
        showHoverStyles: {
            type: Boolean,
            default: false,
        },
        /**
         * One of [ SIZES.SMALL, SIZES.MEDIUM ]
         */
        size: {
            type: String,
            default: SIZES.MEDIUM,
            validator(size) {
                return VALID_SIZES.includes(size);
            },
        },
    },
    data() {
        return {
            isActive: false,
        };
    },
    computed: {
        classes() {
            return {
                /* Small size */
                'vf-secondary-text': this.size === SIZES.SMALL,
                'vf-dropdown-button--small': this.size === SIZES.SMALL,
                /* Medium size */
                'vf-primary-text': this.size === SIZES.MEDIUM,
                'vf-dropdown-button--medium': this.size === SIZES.MEDIUM,
                /* Accent color */
                'vf-dropdown-button--accent': this.palette === PALETTES.ACCENT,
                'vf-dropdown-button--accent--hovered':
                    this.palette === PALETTES.ACCENT && this.showHoverStyles,
                'vf-dropdown-button--accent--active':
                    this.palette === PALETTES.ACCENT && this.isActive,
                /* Other */
                'vf-dropdown-button--icon-right': this.alignIconRight,
            };
        },
        iconProps() {
            return {
                class: 'vf-dropdown-button__icon',
            };
        },
    },
    methods: {
        deactivate() {
            this.isActive = false;
        },
        handleClick() {
            this.isActive = true;
            window.clearTimeout(this.tid);
            this.clickedAt = Date.now();
        },
        handleRelease() {
            if (!this.isActive) {
                return;
            }
            /**
             * Ensure that a tap of the mouse will still do at least a full transition
             * but allow it to transition out immediately when released if held
             **/
            const timeout = Math.max(
                this.clickedAt + TRANSITION_EXIT_WAIT - Date.now(),
                0,
            );
            setTimeout(() => {
                this.deactivate();
            }, timeout);
        },
        handleKeydown() {
            this.handleClick();
            this.tid = window.setTimeout(() => {
                this.deactivate();
            }, TRANSITION_EXIT_WAIT);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../css/variables/module';

.vf-dropdown-button {
    @include rem(min-height, 2.6);

    display: flex;
    align-items: center;
    white-space: nowrap;
    border-style: solid;
    border-width: 2px;
    border-radius: 2px;
    // stylelint-disable-next-line declaration-colon-newline-after
    transition:
        border-color map-get($transitions, 'super-fast'),
        background-color map-get($transitions, 'super-fast'),
        fill map-get($transitions, 'super-fast'),
        stroke map-get($transitions, 'super-fast');
}

.vf-dropdown-button--small {
    @include rem(padding, 0.2);
}

.vf-dropdown-button--medium {
    @include rem(padding, 0.6 0.8);
}

.vf-dropdown-button__text {
    position: relative;
    flex-grow: 1;
    text-align: left;

    @include rem(margin, 0 0.5);
}

.vf-dropdown-button ::v-deep .vf-dropdown-button__icon {
    @include rem(margin-right, 0.7);
    @include rem(width, 1.8);
    @include rem(height, 1.8);
}

.vf-dropdown-button--icon-right ::v-deep .vf-dropdown-button__icon {
    @include rem(margin-left, 0.7);

    margin-right: 0;
}

.vf-dropdown-button--small ::v-deep .vf-dropdown-button__icon {
    margin: 0;
}

.vf-dropdown-button--icon-right ::v-deep .vf-dropdown-button__text {
    order: -1;
    text-align: right;
}

.vf-dropdown-button--accent {
    @include dark-text($default-palette);
    @include background($default-palette, border-color);
    @include background($default-palette);

    &--hovered:not([disabled]),
    &:not([disabled]):focus,
    &:not([disabled]):hover {
        border-color: transparent;

        @include var(color, 'accent-color-dark');
        @include var(background-color, 'accent-color-10');

        ::v-deep .vf-dropdown-button__icon {
            @include var(color, 'accent-color');
        }
    }

    &:not([disabled]):focus {
        @include var(border-color, 'accent-color');
    }

    &[disabled] {
        @include var(color, 'primary-color-20');

        cursor: default;
    }

    &--active:not([disabled]),
    &--active:not([disabled]):hover,
    &--active:not([disabled]):focus {
        @include var(background-color, 'accent-color-20');
    }
}
</style>
